import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

interface UnmarkedClimbDialogProps {
  open: boolean;
  /**
   * What the picture is still missing, named as the caption on it names them —
   * "Start", "Top", a line. Shown so the question is about this climb rather
   * than about drawings in general.
   */
  missing: string[];
  onConfirm: () => void;
  onDismiss: () => void;
}

/**
 * Putting the climb up with nothing drawn on its photo: asked about, not
 * refused.
 *
 * The drawing is the one thing a setter is urged to do and not held to (see
 * `isDrawingAdvice`), and the two ways of saying so both fail on their own. A
 * save that goes straight through says nothing, and a setter who meant to
 * circle the holds finds out from the climb's own page; a save that refuses
 * turns "not yet" into "never", since a climb that would not go up is worth
 * less than one nobody has drawn.
 *
 * So the press is answered with the question, once, and the weight is
 * deliberately the wrong way round for what is being confirmed: drawing it is
 * the solid button and going on is the quiet one. Nobody is stopped — one more
 * press and the climb is up — but the work is put to them first. The same shape
 * as the photo's own way out, for the same reason (see `ClimbPhotoSource`).
 */
export function UnmarkedClimbDialog({
  open,
  missing,
  onConfirm,
  onDismiss,
}: UnmarkedClimbDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onDismiss()}>
      <DialogContent className="sm:max-w-sm" data-testid="climb-unmarked-confirm">
        <DialogHeader>
          <DialogTitle>{t('climbEditor.unmarked.title')}</DialogTitle>
          <DialogDescription>{t('climbEditor.unmarked.note')}</DialogDescription>
        </DialogHeader>

        {/* What is left, put together here rather than interpolated into the
            sentence — the same list the caption on the photo carries, in the
            page's own order. */}
        {missing.length > 0 && (
          <p className="text-sm font-medium" data-testid="climb-unmarked-missing">
            {t('climbEditor.missing.suggestion')} {missing.join(', ')}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <Button type="button" onClick={onDismiss}>
            {t('climbEditor.unmarked.back')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground"
            data-testid="climb-unmarked-confirmed"
            onClick={onConfirm}
          >
            {t('climbEditor.unmarked.anyway')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
